import { NON_MUTABLE_STATUSES } from '@/constants/contract_status'

// Regras de leitura do fechamento mensal de competência.
//
// O servidor é a única fonte da verdade: ele devolve `can_edit`,
// `can_change_extraordinarily` e `lock_reason` em `/api/contracts/search` e em
// `/api/contracts/{id}/details/general_information`, e responde HTTP 423 em qualquer
// escrita bloqueada. As funções abaixo só traduzem essa resposta para a interface.
//
// Nada aqui recalcula a competência a partir de datas: a UI nunca decide sozinha se um
// contrato está travado.

/**
 * Competência do contrato encerrada (após o 3º dia útil do mês seguinte).
 */
export const isCompetenceClosed = contract => contract?.is_competence_closed === true

/**
 * Status que tiram o contrato do universo mutável (excluído ou aguardando exclusão).
 */
export const isLockedStatus = contract => {
  const status = Number(contract?.id_contract_status)

  return NON_MUTABLE_STATUSES.includes(status)
}

/**
 * Fail-open de propósito: enquanto o backend do fechamento não estiver publicado,
 * `can_edit` chega `undefined` e a tela continua utilizável. Quem realmente bloqueia é o
 * servidor (423) — a UI só antecipa o bloqueio quando ele diz explicitamente `false`.
 */
export const serverAllowsEdit = contract => contract?.can_edit !== false

/**
 * Competência fechada com edição restrita à troca entre status válidos (ex.: Rodando e Pago →
 * Cancelado após rodando), que não move o Faturômetro. Datas seguem congeladas e os destinos
 * possíveis vêm em `allowed_status_ids`.
 */
export const isStatusOnlyEdit = contract => contract?.edit_mode === 'status_only'

/**
 * A exclusão continua travada em toda competência fechada, inclusive no modo `status_only`
 * (em que `can_edit` chega `true`).
 */
export const serverAllowsDelete = contract => serverAllowsEdit(contract) && !isCompetenceClosed(contract)

/**
 * Só é verdadeiro quando a competência está fechada E o usuário tem a habilidade
 * `contract_closing_exception:manage`. O servidor já combina as duas condições.
 */
export const serverAllowsExtraordinary = contract => contract?.can_change_extraordinarily === true

/**
 * Formata "2026-07" como "07/2026".
 *
 * Nunca usar `new Date('2026-07')` / `new Date('2026-07-01')` aqui: strings de data no
 * formato ISO são interpretadas como UTC e, em UTC-3, voltam para o mês anterior.
 */
export const competenceLabel = competence => {
  if (typeof competence !== 'string' || !competence) return ''
  const [year, month] = competence.split('-')
  if (!year || !month) return competence

  return `${month}/${year}`
}

/**
 * Texto exibido no tooltip do item de menu desabilitado. O usuário precisa entender o
 * motivo do bloqueio, por isso os itens ficam visíveis e desabilitados — nunca ocultos.
 */
export const lockTooltip = (contract, action = 'edit') => {
  if (isLockedStatus(contract)) {
    return 'Contrato em status não editável. Nenhuma alteração é permitida.'
  }

  const allowed = action === 'delete' ? serverAllowsDelete(contract) : serverAllowsEdit(contract)
  const label = competenceLabel(contract?.competence)
  const closed = label ? `Competência ${label} fechada.` : 'Competência fechada.'

  if (allowed) {
    return action === 'edit' && isStatusOnlyEdit(contract)
      ? `${closed} Só é possível trocar entre status válidos; datas exigem alteração extraordinária.`
      : ''
  }
  if (contract?.lock_reason) return contract.lock_reason

  return `${closed} A alteração só é possível por alteração extraordinária.`
}
